import React, { useState, useEffect } from "react";
import axios from "axios";
import "./UserDashboard.scss";
import IconDashboard from "../../common/icons/IconDashboard";
import IconClose from "../../common/icons/IconClose";
import IconRequest from "../../common/icons/IconRequest";
import IconCost from "../../common/icons/IconCost";
import ShowDetails from "../show-details/ShowDetails";
import { AI_BASE_URL } from "../../config/index";

// Load environment variable

const UserDashboard = ({ userId, token, user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [userData, setUserData] = useState({
    totalRequests: 0,
    totalCost: 0,
    allRequests: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const recordsPerPage = 5;

  const fetchUserReviews = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${AI_BASE_URL}/reviews/user/${userId}`); // Use the AI base URL
      const responseData = response.data;

      // Handle the paginated response structure
      const reviews = responseData.data || [];
      
      const totalRequests = reviews.length;
      const totalCost = reviews.reduce((acc, review) => acc + (review.cost || 0), 0);

      setUserData({
        totalRequests,
        totalCost,
        allRequests: reviews,
      });
    } catch (error) {
      console.error("Error fetching user reviews:", error);
      // Set default values on error
      setUserData({
        totalRequests: 0,
        totalCost: 0,
        allRequests: [],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowClick = (req) => {
    const formattedData = {
      body: {
        answer: req.answer,
        answered: true,
        sources: req.sources || [],
        input: req.question,
      },
    };
    setSelectedRequest(formattedData);
  };

  useEffect(() => {
    if (isOpen) {
      fetchUserReviews();
    }
  }, [isOpen]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("fr-BE", {
      day: "2-digit", // 20, 21, 22, ...
      month: "long", // "décembre", "janvier", etc.
      hour: "2-digit", // 16, 17, 18, ...
      minute: "2-digit", // 28, 30, 45, ...
      hour12: false, // 24-hour format
    });
  };

  // Calculate average cost per request
  const averageCost = userData.totalRequests
    ? (userData.totalCost / userData.totalRequests).toFixed(5)
    : 0;

  const renderStars = (rating) => {
    const totalStars = 5;
    return (
      <div className="star-rating">
        {Array.from({ length: totalStars }).map((_, index) => (
          <svg
            key={index}
            fill={index < rating ? "rgb(24, 160, 251)" : "#ccc"}
            width="22"
            height="22"
            viewBox="0 0 24 24"
            className="star-svg"
          >
            <path
              fill={index < rating ? "rgb(24, 160, 251)" : "#ccc"}
              stroke="none"
              strokeMiterlimit="10"
              strokeWidth="0"
              d="M12,17.27L18.18,21l-1.64-7.03L22,9.24l-7.19-0.61L12,2L9.19,8.63L2,9.24l5.46,4.73L5.82,21L12,17.27z"
            ></path>
          </svg>
        ))}
      </div>
    );
  };

  // Pagination logic
  const totalPages = Math.ceil(userData.allRequests.length / recordsPerPage);
  const paginatedRequests = userData.allRequests.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  return (
    <div>
      {/* Floating Button */}
      <button className="floating-button" onClick={() => setIsOpen(!isOpen)}>
        <IconDashboard />
      </button>

      {/* Overlay and Panel */}
      {isOpen && (
        <>
          <div className="overlay" onClick={() => setIsOpen(false)}></div>
          <div className="panel">
            {/* Close Button */}
            <button className="close-button" onClick={() => setIsOpen(false)}>
              <IconClose />
            </button>
            <h1>Tableau de Bord</h1> {/* Dashboard */}
            {/* Statistics Section */}
            <h2>Vos Statistiques</h2> {/* Your Statistics */}
            <div className="stats">
              <div className="stat-card">
                <div className="card-content">
                  <div>
                    <h4>Total des Requêtes</h4> {/* Total Requests */}
                    <p>{userData.totalRequests}</p>
                  </div>
                  <div className="icon-container">
                    <IconRequest />
                  </div>
                </div>
              </div>
            </div>
            <br />
            {/* Table */}
            <h3>Requêtes Récentes</h3> {/* Recent Requests */}
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Chargement de vos requêtes...</p>
              </div>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>Question</th>
                      <th>Réponse</th>
                      <th>Évaluation</th>
                      <th>Date et Heure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRequests.map((req, index) => (
                      <tr key={req.id} onClick={() => handleRowClick(req)}>
                        <td>
                          <p className="answer">{req.question}</p>
                        </td>
                        <td>
                          <p className="answer">{req.answer}</p>
                        </td>
                        <td className="rating">{renderStars(req.rating)}</td>
                        <td>{formatDate(req.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination Controls */}
                <div className="pagination">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    Précédent
                  </button>
                  <span>
                    Page {currentPage} sur {totalPages}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                  >
                    Suivant
                  </button>
                </div>
              </>
            )}
            {selectedRequest && (
              <ShowDetails
                user={user}
                token={token}
                data={selectedRequest}
                onClose={() => setSelectedRequest(null)}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserDashboard;
